import { useContext, useEffect, useMemo, useState } from 'react';

import { SelectOption } from '@/shared/types';
import { Image } from '@/shared/ui';

import { BlockTitle } from '@/components/old';

import { ServicesContext } from '@/contexts/Services/ServicesContext';

import { DictionaryPermitType, ERZPermit, UserServicePaymentType, Weapon } from '@/api/types';

import api from '@/api';

import { FormStepButtonId } from '../../../../../../../components/Forms/Buttons';
import Select from '../../../../../../../components/Forms/Select';
import { Loader } from '@/shared/ui';
import { StepsNavigationName } from '../../../../../../../config/services';
import ServiceAvailabilityContext, {
  Reason,
} from '../../../../../../../contexts/ServiceAvailability/ServiceAvailabilityContext';
import { PermitListWithWeapons } from '../../../../../components/PermitListWithWeapons/PermitListWithWeapons';
import { currentServiceCode } from '../../PermissionRenewal';

import { PermitFormDataProps } from './types';

interface Props {
  formData: PermitFormDataProps;
  handleInputChange: (name: string, value: unknown) => void;
  permitTypesList: DictionaryPermitType[];
}
const defaultSelectedExtractType: SelectOption = {
  id: '',
  label: 'Всі дозволи',
};

const Step2: React.FC<Props> = ({ formData, handleInputChange, permitTypesList }) => {
  const { disableCause } = useContext(ServiceAvailabilityContext);
  const { getServiceByCode } = useContext(ServicesContext);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [extractTypeEntities, setExtractTypeEntities] = useState<SelectOption[]>([]);
  const [selectedExtractTypeEntity, setSelectedExtractTypeEntity] = useState<SelectOption>(defaultSelectedExtractType);
  const [permitList, setPermitList] = useState<ERZPermit[]>([]);
  const [selectedPermitId, setSelectedPermitId] = useState<Nullable<string>>(
    formData.permitType ? JSON.parse(formData.permitType).id : null,
  );
  const [userWeapons, setUserWeapons] = useState<Weapon[]>([]);

  const filteredPermitList = useMemo<ERZPermit[]>(() => {
    return selectedExtractTypeEntity.id
      ? permitList.filter((permit) => permit.permitType === selectedExtractTypeEntity.id)
      : permitList;
  }, [selectedExtractTypeEntity, permitList]);

  const handleWeaponSelect = (id: ERZPermit['id'], permitType: ERZPermit['permitType']): void => {
    setSelectedPermitId(id);

    const foundService = getServiceByCode(permitType);
    const foundServicePaymentType = foundService?.paymentType;
    const shouldPaymentBeAccessible =
      foundServicePaymentType !== UserServicePaymentType.WITHOUT_PAYMENT && foundServicePaymentType !== null;

    handleInputChange(
      'permitType',
      permitList.find((x) => {
        return x.id === id;
      }),
    );
    handleInputChange('shouldPaymentBeAccessible', shouldPaymentBeAccessible);
  };

  useEffect(() => {
    setIsLoading(true);
    Promise.all([api.services.servicePermits(currentServiceCode), api.getWeaponsData()])
      .then(([{ data: userPermits = [] }, { data: responseWeapons }]) => {
        const availablePermits = userPermits;

        if (!availablePermits.length) {
          disableCause({ reason: Reason.NOT_ENOUGH_DATA });
        } else {
          setExtractTypeEntities([
            defaultSelectedExtractType,
            ...permitTypesList
              .filter((permitType) => availablePermits.some((permit) => permit.permitType === permitType.label))
              .map((permitType) => ({
                id: permitType.label,
                label: permitType.name,
              })),
          ]);
          setPermitList(availablePermits);
          setUserWeapons([...responseWeapons.criminalItems, ...responseWeapons.ipsItems]);
        }
      })
      .finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disableCause]);

  useEffect(() => {
    const nextBtn = document.querySelector(`button#${FormStepButtonId.next}`) as HTMLButtonElement;
    if (!selectedPermitId) {
      nextBtn.disabled = true;
    } else {
      nextBtn.disabled = false;
    }
  }, [selectedPermitId]);

  return (
    <>
      <BlockTitle id="step-description" paddingBottomMdExtra bgLight>
        {StepsNavigationName.permissionData}
        <div className="input-select-top">
          <div className="input-select-top__label">Фільтр</div>
          <div className="input-select-top__input-container">
            <Select
              name="filterPermitType"
              className="value-container-padding-0"
              options={extractTypeEntities}
              defaultValue={selectedExtractTypeEntity}
              placeholder="Не вибрано"
              onChange={setSelectedExtractTypeEntity}
            />
          </div>
        </div>
      </BlockTitle>

      <div className="permit-container">
        {!isLoading &&
          !!filteredPermitList.length &&
          filteredPermitList.map((permit) => (
            <div key={permit.id} className="permit-list">
              <div className="permit-item_input">
                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <label className="radio" htmlFor={permit.id.toString()}>
                  <input
                    type="radio"
                    className="radio__input"
                    id={permit.id.toString()}
                    name="permitType"
                    checked={selectedPermitId === permit.id}
                    onChange={(): void => handleWeaponSelect(permit.id, permit.permitType)}
                  />
                  <span className="radio__checkmark"></span>
                  <span className="radio__label"></span>
                </label>
              </div>

              <PermitListWithWeapons
                erzExtracts={[permit]}
                userWeapons={userWeapons}
                getExtractTypeName={(): string =>
                  extractTypeEntities.find((extractTypeEntity) => {
                    return extractTypeEntity.id === permit.permitType;
                  })?.label || ''
                }
                fieldNames={{
                  permitType: 'Тип дозволу',
                  num: 'Номер дозволу',
                  issuedAt: 'Дата видачі',
                  expiredAt: 'Термін дії',
                }}
              />
            </div>
          ))}
        {!filteredPermitList.length && !isLoading && (
          <div className="information">
            <Image src="/images/sad_smile.svg" alt="no data icon" />
            <BlockTitle size="sm" supportive>
              Немає доступних дозволів
            </BlockTitle>
          </div>
        )}
        {isLoading && (
          <div style={{ position: 'relative', width: '100%', height: '300px' }}>
            <Loader centered />
          </div>
        )}
      </div>
    </>
  );
};

export default Step2;
